import { EllipsisAutoPositionTooltipSpan, GetColor, TooltipBodyDirection } from 'venn-ui-kit';
import React, { type ReactNode, useMemo } from 'react';
import styled from 'styled-components';
import { getFixedWidthElementsWidth } from './allocation-panel-utils';
import type { SubjectRangeAnalysis } from 'venn-api';
import { assertExhaustive, type HistoricalPortfolioNodeAllocationType } from 'venn-utils';
import { AllocationItemHistoricalTooltipContent } from './AllocationItemHistoricalTooltipContent';
import { StyledLabel, StyledSymbol } from './allocation-item-name-tooltip-shared';

type AllocationItemConfig =
  | {
      allocationPanelType: 'historical read-only';
      _tag: 'fund';
      rangeAnalysis: SubjectRangeAnalysis;
    }
  | {
      allocationPanelType: 'historical read-only';
      _tag: 'strategy';
    }
  | {
      allocationPanelType: 'full';
      _tag: 'strategy';
    }
  | {
      allocationPanelType: 'full';
      _tag: 'fund';
    };

export type AllocationItemNameTooltipProps = {
  originalName: string | undefined;
  name: string;
  width: number;
  isGhost: boolean;
  allocationItemConfig: AllocationItemConfig;
  isSelected: boolean;
  isOutsideOfSelectedSubtree: boolean;
  investmentTimeRange: string | undefined;
  isMissingReturnsMessage: JSX.Element | undefined;
  investmentSymbol: string | undefined;
  hasAddFund: boolean;
  hideComparisonColumn: boolean;
  historicalPortfolioNodeAllocationType: HistoricalPortfolioNodeAllocationType | undefined;
  children?: ReactNode;
};

type BasicTooltipContentProps = {
  investmentSymbol: string | undefined;
  contentHeader: string;
  isMissingReturnsMessage: React.ReactNode;
};

const BasicTooltipContent = ({
  investmentSymbol,
  contentHeader,
  isMissingReturnsMessage,
}: BasicTooltipContentProps) => {
  return (
    <TooltipContent>
      <div>
        {investmentSymbol ? <StyledSymbol>{investmentSymbol}</StyledSymbol> : null}
        <StyledLabel inverted>{contentHeader}</StyledLabel>
      </div>
      {isMissingReturnsMessage}
    </TooltipContent>
  );
};

export const AllocationItemNameTooltip = ({
  originalName,
  name,
  width,
  isOutsideOfSelectedSubtree,
  isSelected,
  investmentTimeRange,
  isMissingReturnsMessage,
  allocationItemConfig: item,
  isGhost,
  investmentSymbol,
  hasAddFund,
  hideComparisonColumn,
  historicalPortfolioNodeAllocationType,
  children,
}: AllocationItemNameTooltipProps) => {
  const isStrategy = item._tag === 'strategy';
  const hasChanged = !!originalName && name !== originalName;
  const fixedWidthElementsWidth = getFixedWidthElementsWidth({ isStrategy, hasAddFund, hideComparisonColumn });
  // If it's an investment, we will always show tooltip for adding investment time range
  const alwaysShowTooltip = !isStrategy && !isGhost;
  const contentHeader = investmentTimeRange ? `${name} ${investmentTimeRange}` : name;
  const tooltipContent = useMemo(() => {
    const basicTooltipContent = (
      <BasicTooltipContent
        investmentSymbol={investmentSymbol}
        contentHeader={contentHeader}
        isMissingReturnsMessage={isMissingReturnsMessage}
      />
    );

    // Missing returns message takes precedence over available analysis range
    if (isMissingReturnsMessage) {
      return basicTooltipContent;
    }

    switch (item.allocationPanelType) {
      case 'historical read-only':
        switch (item._tag) {
          case 'fund':
            return (
              <AllocationItemHistoricalTooltipContent
                investmentSymbol={investmentSymbol}
                contentHeader={name}
                rangeAnalysis={{
                  historicalStart: item.rangeAnalysis.historicalStart,
                  historicalEnd: item.rangeAnalysis.historicalEnd,
                  frequency: item.rangeAnalysis.frequency,
                }}
                historicalPortfolioNodeAllocationType={historicalPortfolioNodeAllocationType}
              />
            );
          case 'strategy':
            return basicTooltipContent;
          default:
            return assertExhaustive(item, `Unexpected allocation item type: ${item}`);
        }
      case 'full':
        return basicTooltipContent;
      default:
        return assertExhaustive(item, `Unexpected allocation item type: ${item}`);
    }
  }, [investmentSymbol, contentHeader, isMissingReturnsMessage, item, name, historicalPortfolioNodeAllocationType]);
  return (
    <LeftTextAlign>
      <EllipsisAutoPositionTooltipSpan
        usePortal
        content={tooltipContent}
        bodyDirection={TooltipBodyDirection.Right}
        maxWidth={width - fixedWidthElementsWidth}
        alwaysShowTooltip={alwaysShowTooltip}
        tooltipMaxWidth={172}
      >
        <NameContainer>
          <Name
            hasChanged={hasChanged}
            isOutsideOfSelectedSubtree={isOutsideOfSelectedSubtree}
            isSelected={isSelected}
            data-testid="qa-allocation-item-name"
          >
            {name}
          </Name>
          <ChildrenContainer>{children}</ChildrenContainer>
        </NameContainer>
      </EllipsisAutoPositionTooltipSpan>
    </LeftTextAlign>
  );
};

const LeftTextAlign = styled.div`
  text-align: left;
`;

const TooltipContent = styled.div`
  > div:last-child {
    :not(:only-child) {
      margin-top: 5px;
    }
  }
`;

const Name = styled.span<{ hasChanged: boolean; isOutsideOfSelectedSubtree: boolean; isSelected: boolean }>`
  line-height: normal;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${({ hasChanged, isOutsideOfSelectedSubtree, isSelected }) =>
    hasChanged && !isOutsideOfSelectedSubtree && !isSelected
      ? GetColor.HighlightDark
      : hasChanged && isOutsideOfSelectedSubtree && !isSelected
        ? GetColor.HighlightLight
        : undefined};
`;

const NameContainer = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
`;

const ChildrenContainer = styled.div`
  margin: 0px 4px;
  display: flex;
  align-items: center;
  white-space: nowrap;
`;
